import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../utils/auth";

const getStoredEngineerCustomerAccess = () => {
  const v = localStorage.getItem("engineerCustomerViewEnabled");
  if (v === "true") return true;
  if (v === "false") return false;
  return null;
};

const resolveEngineerNotificationRoute = (notification) => {
  if (!notification) return "/engineer-dashboard";
  if (notification.type === "complaint-assigned" && notification.relatedId) return "/engineer-complaints";
  if ((notification.type === "report-approved" || notification.type === "report-submitted") && notification.relatedId) return `/my-reports/${notification.relatedId}`;
  if (notification.type === "report-changes-requested" && notification.relatedId) return `/create-report/${notification.relatedId}`;
  if (notification.relatedId) return `/my-reports/${notification.relatedId}`;
  return "/engineer-dashboard";
};

export default function EngineerLayout({ children }) {
  const rawEngineerName = (localStorage.getItem("engineerName") || "").trim();
  const storedEngineerId = (localStorage.getItem("engineerId") || "").trim();
  const storedEngineerEmail = (localStorage.getItem("engineerEmail") || "").trim();
  const engineerName = rawEngineerName && rawEngineerName.toLowerCase() !== "engineer"
    ? rawEngineerName
    : storedEngineerId || storedEngineerEmail || "Engineer";
  const engineerLookup  = localStorage.getItem("engineerId") || localStorage.getItem("engineerEmail") || engineerName;
  const engineerAliases = [localStorage.getItem("engineerId"), localStorage.getItem("engineerEmail"), engineerName].filter(Boolean).join(",");
  
  const navigate  = useNavigate();
  const location  = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [expandedSection, setExpandedSection] = useState(null);
  const [profileOpen, setProfileOpen]                 = useState(false);
  const [notifOpen, setNotifOpen]                     = useState(false);
  const [notifications, setNotifications]             = useState([]);
  const [customerViewEnabled, setCustomerViewEnabled] = useState(getStoredEngineerCustomerAccess);

  const desktopNotifRef = useRef(null);
  const mobileNotifRef  = useRef(null);
  const desktopProfileRef = useRef(null);
  const mobileProfileRef  = useRef(null);
  const sidebarRef = useRef(null);

  /* load notifications */
  useEffect(() => {
    axios.get(API_BASE_URL + "/api/notifications", { params: { role: "engineer", user: engineerAliases } })
      .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotifications([]));
  }, [engineerAliases]);

  /* outside-click close */
  useEffect(() => {
    const h = (e) => {
      const clickedNotification =
        desktopNotifRef.current?.contains(e.target) ||
        mobileNotifRef.current?.contains(e.target);
      const clickedProfile =
        desktopProfileRef.current?.contains(e.target) ||
        mobileProfileRef.current?.contains(e.target);
      const clickedSidebar = sidebarRef.current?.contains(e.target);

      if (!clickedNotification) setNotifOpen(false);
      if (!clickedProfile) setProfileOpen(false);

      if (window.innerWidth < 1024 && !clickedSidebar && !e.target.closest("button[data-sidebar-toggle]")) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, []);

  /* auto-expand active accordion section based on route */
  useEffect(() => {
    const p = location.pathname;
    if (["/engineer-complaints", "/engineer/create-complaint", "/my-reports", "/engineer/earth-pit"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("my-work");
    } else if (["/engineer/travel", "/engineer/daily-update"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("operations");
    } else if (["/engineer/vouchers"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("accounts");
    } else if (["/engineer/customers", "/engineer/customer-groups"].some(path => p === path || p.startsWith(`${path}/`))) {
      setExpandedSection("contacts");
    }
  }, [location.pathname]);

  /* customer view feature flag */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!engineerLookup) { setCustomerViewEnabled(false); localStorage.setItem("engineerCustomerViewEnabled", "false"); return; }
        const res = await axios.get(API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}`);
        if (!alive) return;
        const v = Boolean(res.data?.customerViewEnabled);
        setCustomerViewEnabled(v); localStorage.setItem("engineerCustomerViewEnabled", String(v));
      } catch { if (alive) { setCustomerViewEnabled(false); localStorage.setItem("engineerCustomerViewEnabled", "false"); } }
    })();
    return () => { alive = false; };
  }, [engineerLookup]);

  useEffect(() => {
    if (customerViewEnabled === false && (location.pathname.startsWith("/engineer/customers") || location.pathname.startsWith("/engineer/customer-groups")))
      navigate("/engineer-dashboard", { replace: true });
  }, [customerViewEnabled, location.pathname, navigate]);

  const unreadCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);

  const markRead = useCallback(async (notification) => {
    try {
      if (notification?._id && !notification.isRead) {
        await axios.put(API_BASE_URL + `/api/notifications/${notification._id}/read`);
        setNotifications(c => c.map(n => n._id === notification._id ? { ...n, isRead: true } : n));
      }
    } catch (e) { console.error(e); }
    finally { setNotifOpen(false); navigate(resolveEngineerNotificationRoute(notification)); }
  }, [navigate]);

  const clearAllNotifications = async () => {
    try {
      await axios.delete(API_BASE_URL + "/api/notifications", {
        params: { role: "engineer", user: engineerAliases }
      });
      setNotifications([]);
    } catch (e) { console.error(e); }
  };

  const handleLogout = () => {
    logout(navigate);
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Overlay for Mobile/Collapsed Sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          id="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Drawer */}
      <aside 
        ref={sidebarRef}
        className={`fixed left-0 top-0 bottom-0 flex flex-col py-6 bg-gray-900 text-gray-100 w-[280px] z-50 overflow-y-auto transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`} 
        id="sidebar"
      >
        <div className="px-6 mb-8 flex justify-between items-center">
          <div>
            <h1 className="font-headline-lg text-headline-lg font-bold text-white">GENMECH</h1>
            <p className="font-label-caps text-label-caps text-gray-400 uppercase tracking-widest mt-1">Engineer</p>
          </div>
          <button 
            className="text-gray-400 hover:text-white lg:hidden flex items-center justify-center" 
            onClick={() => setSidebarOpen(false)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="flex-1 space-y-1">
          {/* Dashboard Link */}
          <NavLink 
            to="/engineer-dashboard"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `mx-2 py-2 px-4 flex items-center gap-4 mb-2 rounded-lg transition-colors border-2 ${
              isActive 
                ? "bg-blue-600 text-white border-blue-500 font-bold shadow-md"
                : "text-gray-300 hover:text-white hover:bg-gray-800 border-transparent"
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontVariationSettings: location.pathname === "/engineer-dashboard" ? "'FILL' 1" : "'FILL' 0" }}>dashboard</span>
            <span className="font-label-caps text-label-caps">Dashboard</span>
          </NavLink>

          {/* My Work Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "my-work" ? null : "my-work")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/engineer-complaints", "/engineer/create-complaint", "/my-reports", "/engineer/earth-pit"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-blue-600/10 text-blue-400 border-blue-600/30 font-bold"
                  : "text-gray-300 hover:text-white hover:bg-gray-800 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/engineer-complaints", "/engineer/create-complaint", "/my-reports", "/engineer/earth-pit"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>construction</span>
                <span className="font-label-caps text-label-caps">My Work</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "my-work" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "my-work" && (
              <div className="ml-8 pl-4 border-l border-gray-800 flex flex-col gap-2 mt-2">
                <NavLink 
                  to="/engineer-complaints"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  Complaints &amp; Services
                </NavLink>
                <NavLink 
                  to="/engineer/create-complaint"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  Create Complaint
                </NavLink>
                <NavLink 
                  to="/my-reports"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  My Reports
                </NavLink>
                <NavLink 
                  to="/engineer/earth-pit"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  Earth Pit
                </NavLink>
              </div>
            )}
          </div>

          {/* Operations Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "operations" ? null : "operations")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/engineer/travel", "/engineer/daily-update"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-blue-600/10 text-blue-400 border-blue-600/30 font-bold"
                  : "text-gray-300 hover:text-white hover:bg-gray-800 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/engineer/travel", "/engineer/daily-update"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>engineering</span>
                <span className="font-label-caps text-label-caps">Operations</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "operations" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "operations" && (
              <div className="ml-8 pl-4 border-l border-gray-800 flex flex-col gap-2 mt-2">
                <NavLink 
                  to="/engineer/travel"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  Travel Update
                </NavLink>
                <NavLink 
                  to="/engineer/daily-update"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  Daily Update
                </NavLink>
              </div>
            )}
          </div>

          {/* Contacts Accordion (Conditional on customerViewEnabled) */}
          {customerViewEnabled && (
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === "contacts" ? null : "contacts")}
                className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                  ["/engineer/customers"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                    ? "bg-blue-600/10 text-blue-400 border-blue-600/30 font-bold"
                    : "text-gray-300 hover:text-white hover:bg-gray-800 border-transparent"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/engineer/customers"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>groups</span>
                  <span className="font-label-caps text-label-caps">Contacts</span>
                </div>
                <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "contacts" ? "rotate(180deg)" : "rotate(0deg)" }}>
                  expand_more
                </span>
              </button>
              {expandedSection === "contacts" && (
                <div className="ml-8 pl-4 border-l border-gray-800 flex flex-col gap-2 mt-2">
                  <NavLink 
                    to="/engineer/customers"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                      isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                    }`}
                  >
                    Customer
                  </NavLink>
                  <NavLink 
                    to="/engineer/customer-groups"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                      isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                    }`}
                  >
                    Customer Groups
                  </NavLink>
                </div>
              )}
            </div>
          )}


          {/* Accounts Accordion */}
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setExpandedSection(prev => prev === "accounts" ? null : "accounts")}
              className={`w-[calc(100%-16px)] mx-2 py-2 px-4 flex items-center justify-between rounded-lg transition-colors border-2 ${
                ["/engineer/vouchers"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))
                  ? "bg-blue-600/10 text-blue-400 border-blue-600/30 font-bold"
                  : "text-gray-300 hover:text-white hover:bg-gray-800 border-transparent"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: ["/engineer/vouchers"].some(p => location.pathname === p || location.pathname.startsWith(`${p}/`)) ? "'FILL' 1" : "'FILL' 0" }}>account_balance_wallet</span>
                <span className="font-label-caps text-label-caps">Accounts</span>
              </div>
              <span className="material-symbols-outlined transition-transform duration-200" style={{ transform: expandedSection === "accounts" ? "rotate(180deg)" : "rotate(0deg)" }}>
                expand_more
              </span>
            </button>
            {expandedSection === "accounts" && (
              <div className="ml-8 pl-4 border-l border-gray-800 flex flex-col gap-2 mt-2">
                <NavLink 
                  to="/engineer/vouchers"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `py-1.5 px-3 rounded text-sm transition-colors ${
                    isActive ? "text-blue-400 font-bold bg-blue-600/10" : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  Finance / Voucher Management
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        {/* Profile and Logout links */}
        <div className="mt-auto px-2 space-y-1">
          <NavLink 
            to="/engineer/profile"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `flex items-center gap-4 py-2 px-4 rounded-lg transition-colors border-2 ${
              isActive 
                ? "bg-blue-600 text-white border-blue-500 font-bold" 
                : "text-gray-300 hover:text-white hover:bg-gray-800 border-transparent"
            }`}
          >
            <span className="material-symbols-outlined">settings</span>
            <span className="font-label-caps text-label-caps">Settings</span>
          </NavLink>
          <button 
            onClick={handleLogout}
            className="w-full text-left text-gray-300 hover:text-white hover:bg-gray-800 flex items-center gap-4 py-2 px-4 rounded-lg transition-colors border-2 border-transparent"
          >
            <span className="material-symbols-outlined">logout</span>
            <span className="font-label-caps text-label-caps">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`min-h-screen transition-all duration-300 pb-8 bg-gray-100 ${
        sidebarOpen ? "lg:pl-[280px]" : "lg:pl-0"
      }`}>
        {/* Top Sticky Header */}
        <header className="flex justify-between items-center w-full px-8 py-4 gap-md bg-transparent sticky top-0 z-30 backdrop-blur-md bg-gray-100/80">
          <div className="flex items-center gap-4">
            <button 
              data-sidebar-toggle="true"
              className="p-2 hover:bg-gray-200/50 rounded-full transition-colors active:opacity-80 flex items-center justify-center" 
              onClick={() => setSidebarOpen(prev => !prev)}
            >
              <span className="material-symbols-outlined text-gray-800">menu</span>
            </button>
            <span className="font-label-caps text-label-caps text-gray-600/50">ENGINEER OS</span>
          </div>

          <div className="flex items-center gap-6">
            {/* Notifications Dropdown */}
            <div ref={desktopNotifRef} className="relative">
              <button 
                onClick={() => setNotifOpen(c => !c)}
                className="relative cursor-pointer hover:bg-gray-200/50 transition-colors rounded-full active:opacity-80 flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-gray-800">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 z-30 w-80 max-h-[24rem] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-3 shadow-lg">
                  <div className="mb-2 flex items-center justify-between border-b border-gray-100 pb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Notifications</p>
                    {notifications.length > 0 && (
                      <button onClick={clearAllNotifications} className="text-[11px] font-semibold text-red-500 hover:text-red-700">Clear all</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-500">You're all caught up!</p>
                  ) : notifications.map(n => (
                    <button 
                      key={n._id} 
                      onClick={() => {
                        markRead(n);
                      }} 
                      className={`mb-2 w-full rounded-xl px-3 py-3 text-left transition-colors border ${n.isRead ? "bg-gray-50 border-gray-100" : "bg-blue-50 border-blue-100"}`}
                    >
                      <p className="font-semibold text-gray-900 text-sm leading-snug">{n.title || n.message}</p>
                      {n.title && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.message}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div ref={desktopProfileRef} className="relative">
              <button 
                onClick={() => setProfileOpen(c => !c)}
                className="flex items-center gap-3 bg-white px-4 py-2 rounded-full border border-gray-200 hover:bg-gray-200/50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold text-sm">
                  {engineerName.charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="font-label-caps text-[10px] text-gray-500 leading-none">LOGGED IN</span>
                  <span className="font-body-md text-body-md font-bold text-gray-800 mt-0.5">{engineerName}</span>
                </div>
              </button>
              {profileOpen && (
                <div className="absolute right-0 z-30 mt-2 w-56 rounded-2xl border border-gray-200 bg-white p-2 shadow-lg">
                  <button 
                    onClick={() => { setProfileOpen(false); navigate("/engineer/profile"); }} 
                    className="w-full rounded-xl px-4 py-3 text-left text-sm hover:bg-gray-50 font-semibold text-gray-700 transition-colors"
                  >
                    My Profile
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="w-full rounded-xl px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 font-semibold transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content Canvas */}
        <div className="p-1 sm:p-2 lg:p-3 w-full max-w-none page-canvas">
          {children || <Outlet />}
        </div>

      </main>
    </div>
  );
}
